import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MedicationIcon from '@mui/icons-material/Medication';
import ScienceIcon from '@mui/icons-material/Science';
import LinkIcon from '@mui/icons-material/Link';

const ConsultationPreviewModal = ({ open, onClose, onConfirm, consultationData }) => {
  if (!consultationData) return null;

  const { patient, doctor, prescription, tests_ordered, referrals } =
    consultationData;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 'bold',
          backgroundColor: '#f5f5f5',
          textAlign: 'center',
        }}
      >
        Consultation Summary - {consultationData.consultation_number}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ maxHeight: '80vh', overflowY: 'auto', padding: 3 }}
      >
        {/* Patient & Doctor Details */}
        <Grid container spacing={3}>
          {/* Patient Details */}
          <Grid item xs={12} md={6}>
            <Card variant='outlined'>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  <PersonIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> Patient
                  Details
                </Typography>
                <Typography>
                  <b>Name:</b> {patient?.name || 'N/A'}
                </Typography>
                <Typography>
                  <b>Contact:</b> {patient?.phone_number || 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Doctor Details */}
          <Grid item xs={12} md={6}>
            <Card variant='outlined'>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  <LocalHospitalIcon sx={{ verticalAlign: 'middle', mr: 1 }} />{' '}
                  Doctor Details
                </Typography>
                <Typography>
                  <b>Name:</b> {doctor?.full_name || 'N/A'}
                </Typography>
                <Typography>
                  <b>Phone:</b> {doctor?.phone || 'N/A'}
                </Typography>
                <Typography>
                  <b>Specialties:</b>{' '}
                  {doctor?.specialities?.map((spec) => spec.name).join(', ') ||
                    'N/A'}
                </Typography>
                <Typography>
                  <b>Experience:</b> {doctor?.experience} years
                </Typography>
                <Typography>
                  <b>City:</b> {doctor?.city || 'N/A'}
                </Typography>
                <Typography>
                  <b>Clinic:</b> {doctor?.clinic?.clinic_name || 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Consultation Details */}
        <Card variant='outlined' sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant='h6' gutterBottom>
              <EventNoteIcon sx={{ verticalAlign: 'middle', mr: 1 }} />{' '}
              Consultation Details
            </Typography>
            <Typography>
              <b>Symptoms:</b> {consultationData.symptoms || 'N/A'}
            </Typography>
            <Typography>
              <b>Diagnosis:</b> {consultationData.diagnosis || 'N/A'}
            </Typography>
            <Typography>
              <b>Notes:</b> {consultationData.notes || 'N/A'}
            </Typography>
            <Typography>
              <b>Follow-up Required:</b>{' '}
              {consultationData.follow_up_required ? 'Yes' : 'No'}
            </Typography>
            {consultationData.follow_up_date && (
              <Typography>
                <b>Follow-up Date:</b>{' '}
                {new Date(consultationData.follow_up_date).toLocaleDateString()}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Prescription */}
        {prescription && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='h6'>
                <MedicationIcon sx={{ verticalAlign: 'middle', mr: 1 }} />{' '}
                Prescription
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                <b>Prescription Number:</b>{' '}
                {prescription.prescription_number || 'N/A'}
              </Typography>
              <Typography>
                <b>Created At:</b>{' '}
                {new Date(prescription.created_at).toLocaleString()}
              </Typography>
              {prescription.medications?.length > 0 ? (
                prescription.medications.map((med, index) => (
                  <Typography key={index}>
                    <b>{med.medication_name}</b> - {med.dosage}, {med.frequency}{' '}
                    ({med.instructions || 'No instructions'})
                  </Typography>
                ))
              ) : (
                <Typography>No medications prescribed.</Typography>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Ordered Tests */}
        {tests_ordered?.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='h6'>
                <ScienceIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> Ordered
                Tests
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {tests_ordered.map((test, index) => (
                <Typography key={index}>
                  <b>{test.test_name}</b> -{' '}
                  {test.instructions || 'No instructions'} (Status:{' '}
                  {test.status})
                </Typography>
              ))}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Referrals */}
        {referrals?.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant='h6'>
                <LinkIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> Referrals
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {referrals.map((ref, index) => (
                <Typography key={index}>
                  <b>Referred To:</b> {ref.referred_to} | <b>Reason:</b>{' '}
                  {ref.reason || 'N/A'}
                </Typography>
              ))}
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color='secondary'>Close</Button>
        {onConfirm && (
        <Button
          onClick={() => {
            onConfirm();
            onClose();
          }}
          color="primary"
          variant="contained"
        >
          Confirm & Complete
        </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConsultationPreviewModal;
